import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { formatPhone, validatePhone, COUNTRY_CONFIGS } from '../utils/formatters';

const PAGE_SIZE = 10;
const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    building: 'M3 21h18M3 7v1a3 3 0 006 0V4m0 4a3 3 0 006 0V4m0 4a3 3 0 006 0V7H3',
    user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 100-8 4 4 0 000 8z',
    phone: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
    mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6',
    mapPin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z M12 13a3 3 0 100-6 3 3 0 000 6z',
    chevronLeft: 'M15 18l-6-6 6-6',
    chevronRight: 'M9 18l6-6-6-6',
    refresh: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15',
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4',
    eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z',
    eyeOff: 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94 M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19 M1 1l22 22',
    fileText: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
};

const defaultForm = {
    companyName: '',
    address: '',
    contactPerson: '',
    phone: '',
    countryCode: '',
    email: '',
    password: '',
    status: 'Active',
    industry: '',
    notes: '',
    clientLogo: ''
};

export default function ManageClients({ user, onNavigate, operatorDetails }) {
    const [clients, setClients] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
    const [form, setForm] = useState(defaultForm);
    const [editingId, setEditingId] = useState(null);
    const [toast, setToast] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const addressInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const [showImportScreen, setShowImportScreen] = useState(false);

    // Contract & Document expiry states
    const [contractTitle, setContractTitle] = useState('');
    const [contractStartDate, setContractStartDate] = useState('');
    const [contractEndDate, setContractEndDate] = useState('');
    const [contractValue, setContractValue] = useState('');
    const [contractFile, setContractFile] = useState(null);

    const [docTitle, setDocTitle] = useState('');
    const [docType, setDocType] = useState('Trade License');
    const [docExpiryDate, setDocExpiryDate] = useState('');
    const [docFile, setDocFile] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchClients = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/clients/${user?.operatorId || user?._id || 'all'}`);
            setClients(res.data);
        } catch (err) {
            console.error('Fetch clients error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchIndustries = async () => {
        try {
            const res = await axios.get(`${API}/master-data`);
            setIndustries(res.data.filter(i => i.category === 'industries'));
        } catch (err) {
            console.error('Fetch industries error:', err);
        }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Company Name': 'Test Client Corp',
                'Address': '123 Business Bay, Dubai',
                'Contact Person': 'Jane Smith',
                'Phone': '501122334',
                'Country Code': '+971',
                'Email': 'jane@testclient.com',
                'Industry': 'Technology',
                'Status': 'Active',
                'Notes': 'High priority client'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Clients');
        XLSX.writeFile(wb, 'Clients_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    companyName: row['Company Name'],
                    address: row['Address'],
                    contactPerson: row['Contact Person'],
                    phone: row['Phone'],
                    countryCode: row['Country Code'] || '+971',
                    email: row['Email'],
                    industry: row['Industry'],
                    status: row['Status'] || 'Active',
                    notes: row['Notes']
                }));

                const res = await fetch('/api/clients/import', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operatorId: user?.operatorId || user?._id, clients: mapped })
                });
                const result = await res.json();
                if (res.ok) {
                    showToast(`${result.successCount} clients imported!`);
                    fetchClients();
                } else {
                    showToast(result.message || 'Import failed', 'error');
                }
            } catch (err) {
                showToast('Format error', 'error');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        fetchClients();
        fetchIndustries();
    }, [user]);

    useEffect(() => {
        if (viewMode === 'form' && window.google && addressInputRef.current) {
            const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current);
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place.formatted_address) {
                    setForm(prev => ({ ...prev, address: place.formatted_address }));
                }
            });
        }
    }, [viewMode]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return clients.filter(c => {
            return !q ||
                (c.companyName || '').toLowerCase().includes(q) ||
                (c.contactPerson || '').toLowerCase().includes(q) ||
                (c.email || '').toLowerCase().includes(q) ||
                (c.phone || '').toLowerCase().includes(q) ||
                (c.address || '').toLowerCase().includes(q);
        });
    }, [clients, search]);

    const stats = useMemo(() => {
        return {
            total: filtered.length,
            active: filtered.filter(c => c.status === 'Active').length,
            inactive: filtered.filter(c => c.status === 'Inactive').length,
        };
    }, [filtered]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();

        // Validate phone number based on operator country
        if (form.phone && !validatePhone(form.phone, operatorDetails?.country)) {
            showToast(`Phone number is invalid for ${operatorDetails?.country || 'the selected country'}.`, 'error');
            return;
        }

        setSaving(true);
        try {
            const opId = user?.operatorId || user?._id;
            if (!opId) throw new Error('Operator ID not found. Please log in again.');

            let uploadedLogoName = form.clientLogo;
            if (logoFile) {
                const formData = new FormData();
                formData.append('document', logoFile);
                try {
                    const res = await axios.post(`${API}/upload`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    if (res.data && res.data.filename) {
                        uploadedLogoName = res.data.filename;
                    }
                } catch (e) {
                    console.error('Logo upload error:', e);
                }
            }

            // Upload contract file if attached
            let uploadedContractFile = '';
            if (contractFile) {
                const formData = new FormData();
                formData.append('document', contractFile);
                try {
                    const res = await axios.post(`${API}/upload`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    if (res.data && res.data.filename) {
                        uploadedContractFile = res.data.filename;
                    }
                } catch (e) {
                    console.error('Contract upload error:', e);
                }
            }

            // Upload document file if attached
            let uploadedDocFile = '';
            if (docFile) {
                const formData = new FormData();
                formData.append('document', docFile);
                try {
                    const res = await axios.post(`${API}/upload`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    if (res.data && res.data.filename) {
                        uploadedDocFile = res.data.filename;
                    }
                } catch (e) {
                    console.error('Document upload error:', e);
                }
            }

            const { _id, __v, createdAt, updatedAt, ...sanitized } = form;
            const payload = { ...sanitized, operatorId: opId, clientLogo: uploadedLogoName };

            // Assemble contracts
            const currentContracts = Array.isArray(form.contracts) ? [...form.contracts] : [];
            if (contractTitle.trim() || contractEndDate || uploadedContractFile) {
                currentContracts.push({
                    title: contractTitle.trim() || 'Client Contract Agreement',
                    startDate: contractStartDate ? new Date(contractStartDate) : null,
                    endDate: contractEndDate ? new Date(contractEndDate) : null,
                    value: Number(contractValue) || 0,
                    fileName: uploadedContractFile || '',
                    status: 'Active',
                    createdAt: new Date()
                });
            }
            payload.contracts = currentContracts;

            // Assemble documents
            const currentDocs = Array.isArray(form.documents) ? [...form.documents] : [];
            if (docTitle.trim() || docExpiryDate || uploadedDocFile) {
                currentDocs.push({
                    title: docTitle.trim() || docType || 'Legal Document',
                    type: docType || 'Legal Document',
                    expiryDate: docExpiryDate ? new Date(docExpiryDate) : null,
                    fileName: uploadedDocFile || ''
                });
            }
            payload.documents = currentDocs;

            // For updates, only send password if it's non-empty to avoid overwriting with empty string
            // For new clients, always send whatever is in the form (could be empty string if not required)
            if (editingId && !payload.password) {
                delete payload.password;
            }

            if (editingId) {
                await axios.put(`${API}/clients/${editingId}`, payload);
                showToast('Client updated successfully');
            } else {
                await axios.post(`${API}/clients`, payload);
                showToast('Client added successfully');
            }
            await fetchClients();
            closeModal();
        } catch (err) {
            console.error('Save client error:', err);
            const msg = err.response?.data?.message || err.message || 'Error saving client';
            showToast(msg, 'error');
        } finally {
            setSaving(false);
        }
    };


    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this client?')) return;
        try {
            await axios.delete(`${API}/clients/${id}`);
            showToast('Client deleted successfully');
            fetchClients();
        } catch (err) {
            console.error('Delete client error:', err);
            showToast('Error deleting client', 'error');
        }
    };

    const openModal = (client = null) => {
        if (client) {
            setForm({ ...client, password: client.password || '' });
            setEditingId(client._id);
        } else {
            const config = COUNTRY_CONFIGS[operatorDetails?.country] || Object.values(COUNTRY_CONFIGS)[0];
            setForm({ ...defaultForm, countryCode: config?.phoneCode || '+91' });
            setEditingId(null);
        }
        setLogoFile(null);
        setContractTitle('');
        setContractStartDate('');
        setContractEndDate('');
        setContractValue('');
        setContractFile(null);
        setDocTitle('');
        setDocType('Trade License');
        setDocExpiryDate('');
        setDocFile(null);
        setViewMode('form');
    };

    const closeModal = () => {
        setViewMode('list');
        setForm(defaultForm);
        setLogoFile(null);
        setEditingId(null);
        setContractTitle('');
        setContractStartDate('');
        setContractEndDate('');
        setContractValue('');
        setContractFile(null);
        setDocTitle('');
        setDocType('Trade License');
        setDocExpiryDate('');
        setDocFile(null);
    };

    if (showImportScreen) {
        return (
            <ClientImportWizard
                operatorId={user?.operatorId || user?._id}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchClients(); }}
            />
        );
    }

    return (
        <div className="av-page">
            <style>{`
                .av-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }

                /* Header */
                .av-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .av-title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; letter-spacing: -0.02em; }
                .av-subtitle { font-size: 14px; color: #64748b; margin: 0; font-weight: 500; }
                .av-add-btn {
                    background: #063A5C; color: white; border: none; padding: 12px 24px;
                    border-radius: 12px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 10px;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); box-shadow: 0 4px 12px rgba(6, 58, 92, 0.25); font-size: 14px;
                }
                .av-add-btn:hover { background: #2d2a6b; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(6, 58, 92, 0.3); }

                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #00B2A9 0%, #16539C 100%);
                }
                .btn-primary {
                    background: #063A5C;
                    box-shadow: 0 4px 12px rgba(6, 58, 92, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                /* Stats grid */
                .av-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 24px; }
                .av-stat-card {
                    background: white; padding: 22px; border-radius: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 18px; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
                }
                .av-stat-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px -8px rgba(0,0,0,0.08); border-color: #cbd5e1; }
                .av-stat-icon {
                    width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    font-size: 24px; flex-shrink: 0; transition: all 0.3s;
                }
                .av-stat-card:hover .av-stat-icon { transform: scale(1.1); }
                .av-stat-label { font-size: 13px; font-weight: 600; color: #64748b; margin-bottom: 2px; }
                .av-stat-value { font-size: 24px; font-weight: 800; color: #1e293b; line-height: 1; }

                /* Content Box */
                .av-content {
                    background: white; border-radius: 20px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); overflow: hidden;
                }

                /* Toolbar */
                .av-toolbar {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 20px 24px; border-bottom: 1px solid #f1f5f9; background: #fff;
                }
                .av-toolbar-left { display: flex; align-items: center; gap: 12px; }
                .av-toolbar-left h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
                .av-refresh-btn {
                    border: 1.5px solid #e2e8f0; background: white; padding: 8px; border-radius: 10px;
                    cursor: pointer; color: #64748b; transition: all 0.2s; display: flex; align-items: center;
                }
                .av-refresh-btn:hover { border-color: #063A5C; color: #063A5C; transform: rotate(180deg); }
                
                .av-search-wrap { position: relative; display: flex; align-items: center; gap: 10px; }
                .av-search-wrap svg { position: absolute; left: 14px; color: #94a3b8; }
                .av-search {
                    padding: 10px 16px 10px 42px; border: 2.5px solid #f1f5f9; border-radius: 12px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    width: 320px; transition: all 0.2s; font-family: 'Inter', sans-serif;
                    font-weight: 500;
                }
                .av-search:focus { border-color: #063A5C; background: white; box-shadow: 0 0 0 4px rgba(6, 58, 92, 0.05); }

                /* Table */
                .av-table-wrap { overflow-x: auto; }
                .av-table { width: 100%; border-collapse: collapse; }
                .av-table th {
                    padding: 16px 24px; text-align: left; font-size: 11px; font-weight: 800;
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.12em;
                    border-bottom: 1px solid #e2e8f0; background: #fafbfc;
                }
                .av-table td { padding: 18px 24px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .av-table tr:hover { background: #fbfcfe; }
                .av-table tr:last-child td { border-bottom: none; }

                .av-client-info { display: flex; align-items: center; gap: 14px; }
                .av-client-avatar {
                    width: 44px; height: 44px; background: linear-gradient(135deg, #063A5C 0%, #312e81 100%);
                    color: white; display: flex; align-items: center; justify-content: center;
                    border-radius: 12px; font-weight: 800; font-size: 18px; flex-shrink: 0;
                    box-shadow: 0 4px 10px rgba(6, 58, 92, 0.2);
                }
                .av-company-name { 
                    font-weight: 700; color: #1e293b; font-size: 15px; display: block; margin-bottom: 2px; 
                    text-decoration: none; transition: all 0.2s;
                }
                a.av-company-name:hover { color: #063A5C; text-decoration: underline; cursor: pointer; }
                .av-company-address { font-size: 12px; color: #64748b; font-weight: 500; }

                .av-contact-name { font-weight: 700; color: #334155; font-size: 14px; }
                .av-contact-detail { font-size: 13px; color: #64748b; margin-top: 4px; display: flex; align-items: center; gap: 6px; font-weight: 500; }

                /* Status */
                .av-status-badge {
                    padding: 6px 14px; border-radius: 99px; font-size: 11px; font-weight: 800;
                    display: inline-flex; align-items: center; gap: 6px; text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .av-status-badge.active { background: #f0fdf4; color: #166534; border: 1.5px solid #dcfce7; }
                .av-status-badge.inactive { background: #fef2f2; color: #991b1b; border: 1.5px solid #fee2e2; }
                .av-status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }

                /* Actions */
                .av-actions { display: flex; gap: 8px; }
                .av-act-btn {
                    border: 1.5px solid #e2e8f0; background: white; padding: 8px; cursor: pointer; border-radius: 10px;
                    color: #94a3b8; transition: all 0.2s; display: flex; align-items: center; justify-content: center;
                }
                .av-act-btn:hover { border-color: #063A5C; color: #063A5C; background: #fafbfc; transform: scale(1.05); }
                .av-act-btn.delete:hover { border-color: #ef4444; color: #ef4444; background: #fff5f5; }

                /* Pagination */
                .av-container {
                    padding: 32px;
                    max-width: 1400px;
                    margin: 0 auto;
                    font-family: 'Outfit', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
                    background: #f8fafc;
                    min-height: 100vh;
                }

                /* Header */
                .av-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 28px;
                }
                .av-title {
                    font-size: 26px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                    letter-spacing: -0.02em;
                }
                .av-subtitle {
                    color: #64748b;
                    font-size: 14px;
                    margin: 4px 0 0 0;
                    font-weight: 500;
                }

                /* Stats Cards */
                .av-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
                    gap: 20px;
                    margin-bottom: 28px;
                }
                .av-stat-card {
                    background: white;
                    padding: 22px 24px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.02);
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                .av-stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px rgba(0,0,0,0.04);
                }
                .av-stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .av-stat-label {
                    font-size: 12px;
                    font-weight: 700;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .av-stat-value {
                    font-size: 28px;
                    font-weight: 800;
                    color: #0f172a;
                    margin-top: 4px;
                    line-height: 1.1;
                    letter-spacing: -0.02em;
                }

                /* Content Card */
                .av-content {
                    background: white;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.03);
                }

                /* Toolbar */
                .av-toolbar {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    background: #ffffff;
                }
                .av-toolbar-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .av-toolbar-left h3 {
                    margin: 0;
                    font-size: 17px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: -0.01em;
                }
                .av-refresh-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #64748b;
                    transition: all 0.2s;
                }
                .av-refresh-btn:hover {
                    color: #2563eb;
                    border-color: #bfdbfe;
                    background: #eff6ff;
                }
                .av-search-wrap {
                    position: relative;
                    width: 320px;
                }
                .av-search-wrap svg {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    pointer-events: none;
                }
                .av-search {
                    width: 100%;
                    padding: 10px 16px 10px 42px;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 13.5px;
                    font-weight: 500;
                    outline: none;
                    background: #f8fafc;
                    transition: all 0.2s;
                    box-sizing: border-box;
                    color: #0f172a;
                }
                .av-search:focus {
                    border-color: #2563eb;
                    background: white;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                /* Table */
                .av-table-wrap {
                    overflow-x: auto;
                }
                .av-table {
                    width: 100%;
                    border-collapse: collapse;
                    text-align: left;
                }
                .av-table th {
                    padding: 16px 24px;
                    background: #f8fafc;
                    font-size: 12px;
                    font-weight: 700;
                    color: #475569;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0;
                }
                .av-table td {
                    padding: 18px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 13.5px;
                    color: #334155;
                    vertical-align: middle;
                }
                .av-table tr:hover td {
                    background: #f8fafc;
                }

                .av-client-info {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .av-client-avatar {
                    width: 42px;
                    height: 42px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    color: #2563eb;
                    font-size: 16px;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #bfdbfe;
                    flex-shrink: 0;
                }
                .av-company-name {
                    font-weight: 700;
                    color: #0f172a;
                    font-size: 14.5px;
                    text-decoration: none;
                    display: block;
                    transition: color 0.15s;
                }
                .av-company-name:hover {
                    color: #2563eb;
                }
                .av-company-address {
                    font-size: 12.5px;
                    color: #94a3b8;
                    margin-top: 2px;
                    display: block;
                }

                .av-contact-name {
                    font-weight: 600;
                    color: #0f172a;
                }
                .av-contact-detail {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    color: #64748b;
                    margin-top: 4px;
                }

                .av-status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 700;
                }
                .av-status-badge.active {
                    background: #ecfdf5;
                    color: #059669;
                    border: 1px solid #a7f3d0;
                }
                .av-status-badge.inactive {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fecaca;
                }
                .av-status-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: currentColor;
                }

                .av-actions {
                    display: flex;
                    gap: 8px;
                }
                .av-act-btn {
                    width: 34px;
                    height: 34px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.15s;
                }
                .av-act-btn:hover {
                    color: #2563eb;
                    border-color: #bfdbfe;
                    background: #eff6ff;
                }
                .av-act-btn.delete:hover {
                    color: #dc2626;
                    border-color: #fecaca;
                    background: #fef2f2;
                }

                .av-pagination {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 24px;
                    border-top: 1px solid #f1f5f9;
                    background: #fff;
                }
                .av-page-info { font-size: 13px; color: #64748b; font-weight: 500; }
                .av-page-btns { display: flex; gap: 6px; }
                .av-page-btn {
                    width: 36px; height: 36px; border: 1px solid #e2e8f0; background: white;
                    border-radius: 8px; cursor: pointer; font-size: 13px; color: #475569;
                    transition: all 0.15s; display: flex; align-items: center; justify-content: center; font-weight: 700;
                }
                .av-page-btn:hover:not(:disabled) { border-color: #2563eb; color: #2563eb; background: #eff6ff; }
                .av-page-btn.active { background: #2563eb; color: white; border-color: #2563eb; box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25); }
                .av-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

                /* Modern Form Styling */
                .av-form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
                .av-form-group { display: flex; flex-direction: column; gap: 8px; }
                .av-form-group.full { grid-column: span 2; }
                .av-form-group label { font-size: 13px; font-weight: 700; color: #334155; }

                .av-input-wrap { position: relative; display: flex; align-items: center; }
                .av-input-icon { position: absolute; left: 16px; color: #94a3b8; pointer-events: none; transition: all 0.2s; }
                .av-input-wrap input {
                    width: 100%; padding: 12px 16px 12px 46px; border: 1px solid #cbd5e1;
                    border-radius: 10px; font-size: 14px; outline: none; transition: all 0.2s;
                    background: #ffffff; font-weight: 600; color: #0f172a;
                    box-sizing: border-box; font-family: inherit;
                }
                .av-input-wrap input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }
                .av-input-wrap input:focus + .av-input-icon { color: #2563eb; }

                .av-select {
                    width: 100%; padding: 12px 16px; border: 1px solid #cbd5e1;
                    border-radius: 10px; font-size: 14px; outline: none; transition: all 0.2s;
                    background: #ffffff; font-weight: 600; color: #0f172a; cursor: pointer;
                    appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
                    background-repeat: no-repeat; background-position: right 14px center; background-size: 16px;
                    font-family: inherit;
                }
                .av-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }

                .av-textarea {
                    width: 100%; padding: 12px 16px; border: 1px solid #cbd5e1;
                    border-radius: 10px; font-size: 14px; outline: none; transition: all 0.2s;
                    background: #ffffff; font-weight: 500; color: #0f172a; resize: vertical; min-height: 100px;
                    font-family: inherit; box-sizing: border-box;
                }
                .av-textarea:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }

                .av-eye-btn {
                    position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
                    background: none; border: none; cursor: pointer; color: #94a3b8;
                    display: flex; align-items: center; padding: 4px; border-radius: 6px;
                    transition: color 0.2s;
                }
                .av-eye-btn:hover { color: #2563eb; }

                .av-btn-cancel {
                    padding: 12px 24px; border: 1px solid #e2e8f0; background: white;
                    border-radius: 10px; font-size: 14px; font-weight: 700; color: #64748b;
                    cursor: pointer; transition: all 0.2s;
                }
                .av-btn-cancel:hover { border-color: #cbd5e1; color: #0f172a; background: #f8fafc; }
                
                .av-btn-save {
                    padding: 12px 32px; border: none; background: #2563eb; color: white;
                    border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer;
                    transition: all 0.2s; box-shadow: 0 4px 14px rgba(37, 99, 235, 0.25);
                }
                .av-btn-save:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(37, 99, 235, 0.35); }

                /* Toast */
                .av-toast {
                    position: fixed; top: 24px; right: 24px; padding: 16px 24px; border-radius: 12px;
                    color: white; z-index: 3000; box-shadow: 0 10px 25px rgba(0,0,0,0.12);
                    font-weight: 700; animation: toastSlide 0.3s ease;
                }
                @keyframes toastSlide { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                .av-toast.success { background: #10b981; }
                .av-toast.error { background: #ef4444; }

                .av-empty { text-align: center; padding: 80px 24px; color: #94a3b8; font-size: 14px; font-weight: 600; }

                .av-form-section {
                    grid-column: span 2;
                    margin-top: 10px;
                    margin-bottom: 4px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .av-form-section:first-child { margin-top: 0; }
                .av-form-section h3 {
                    font-size: 13px;
                    font-weight: 800;
                    color: #2563eb;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    margin: 0;
                    white-space: nowrap;
                }
                .av-form-section-line {
                    height: 1px;
                    background: #e2e8f0;
                    flex-grow: 1;
                }

                .premium-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    border-radius: 10px;
                    font-weight: 700;
                    font-size: 13.5px;
                    cursor: pointer;
                    transition: all 0.2s;
                    border: 1px solid transparent;
                    color: white;
                }
                .btn-primary {
                    background: #2563eb;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
                }
                .btn-primary:hover {
                    background: #1d4ed8;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
                }
                .btn-secondary {
                    background: #ffffff;
                    color: #334155;
                    border-color: #e2e8f0;
                }
                .btn-secondary:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                    color: #0f172a;
                }
            `}</style>

            {toast && <div className={`av-toast ${toast.type}`}>{toast.msg}</div>}

            <div className="av-header">
                <div>
                    {viewMode === 'form' ? (
                        <>
                            <button onClick={() => setViewMode('list')} style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'none', color: '#64748b', fontSize: '13px', fontWeight: 700, cursor: 'pointer', transition: '0.2s', marginBottom: 16, padding: 0 }}>
                                <Icon d={icons.chevronLeft} size={16} strokeWidth={2.5} /> Back to Clients
                            </button>
                            <h1 className="av-title">{editingId ? 'Update Client Profile' : 'Register New Corporate Client'}</h1>
                            <p className="av-subtitle">Configure company details and administrative credentials</p>
                        </>
                    ) : (
                        <>
                            <h1 className="av-title">Manage Clients</h1>
                            <p className="av-subtitle">Track and manage your corporate clients and partners</p>
                        </>
                    )}
                </div>
                {viewMode === 'list' && (
                    <div style={{ display: 'flex', gap: 12 }}>
                        <button className="premium-btn btn-secondary" onClick={() => setShowImportScreen(true)}>
                            <Icon d={icons.upload} size={17} strokeWidth={2.2} color="#475569" />
                            Import
                        </button>
                        <button className="premium-btn btn-secondary" onClick={downloadTemplate}>
                            <Icon d={icons.download} size={17} strokeWidth={2.2} color="#475569" />
                            Template
                        </button>
                        <button className="premium-btn btn-primary" onClick={() => openModal()}>
                            <Icon d={icons.plus} size={17} strokeWidth={2.5} />
                            Add New Client
                        </button>
                    </div>
                )}
            </div>

            {viewMode === 'list' && (
                <div className="av-stats-grid">
                    <div className="av-stat-card">
                        <div className="av-stat-icon" style={{ background: '#eff6ff', color: '#063A5C' }}>
                            <Icon d={icons.building} size={28} />
                        </div>
                        <div>
                            <div className="av-stat-label">Total Clients</div>
                            <div className="av-stat-value">{stats.total}</div>
                        </div>
                    </div>
                    <div className="av-stat-card">
                        <div className="av-stat-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                            <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'currentColor' }} />
                        </div>
                        <div>
                            <div className="av-stat-label">Active Companies</div>
                            <div className="av-stat-value">{stats.active}</div>
                        </div>
                    </div>
                    <div className="av-stat-card">
                        <div className="av-stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                            <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'currentColor', opacity: 0.5 }} />
                        </div>
                        <div>
                            <div className="av-stat-label">Inactive</div>
                            <div className="av-stat-value">{stats.inactive}</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="av-content">
                {viewMode === 'list' ? (
                    <>
                        <div className="av-toolbar">
                            <div className="av-toolbar-left">
                                <button className="av-refresh-btn" onClick={fetchClients} title="Refresh Data">
                                    <Icon d={icons.refresh} size={18} strokeWidth={2.5} />
                                </button>
                                <h3>All Companies</h3>
                            </div>
                            <div className="av-search-wrap">
                                <Icon d={icons.search} size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by name, email, or place..."
                                    className="av-search"
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                />
                            </div>
                        </div>

                        <div className="av-table-wrap">
                            <table className="av-table">
                                <thead>
                                    <tr>
                                        <th>Company Details</th>
                                        <th>Contact Person</th>
                                        <th>Contact Info</th>
                                        <th>People</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="6" className="av-empty">Loading clients...</td></tr>
                                    ) : pageData.length === 0 ? (
                                        <tr><td colSpan="6" className="av-empty">No clients found matching your search.</td></tr>
                                    ) : (
                                        pageData.map(client => (
                                            <tr key={client._id}>
                                                <td>
                                                    <div className="av-client-info">
                                                        <div className="av-client-avatar">
                                                            {(client.companyName || '?').charAt(0)}
                                                        </div>
                                                        <div>
                                                            <a href="#" className="av-company-name" onClick={(e) => { e.preventDefault(); onNavigate('client-details', client._id); }}>{client.companyName}</a>
                                                            <span className="av-company-address">{client.address || 'Location not set'}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="av-contact-name">{client.contactPerson || 'N/A'}</div>
                                                </td>
                                                <td>
                                                    <div className="av-contact-detail">
                                                        <Icon d={icons.mail} size={13} strokeWidth={2.5} />
                                                        {client.email || 'No email'}
                                                    </div>
                                                    <div className="av-contact-detail">
                                                        <Icon d={icons.phone} size={13} strokeWidth={2.5} />
                                                        {formatPhone(client.phone, operatorDetails?.country) || 'No phone'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <a 
                                                        href="#"
                                                        onClick={(e) => { e.preventDefault(); onNavigate('client-details', client._id, 'people'); }}
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '8px', transition: 'background 0.15s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                        title="View Passengers"
                                                    >
                                                        <Icon d={icons.user} size={14} color="#2563eb" />
                                                        <span style={{ fontWeight: 700, color: '#2563eb' }}>{(client.contacts || []).length}</span>
                                                        <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b' }}>Person(s)</span>
                                                    </a>
                                                </td>
                                                <td>
                                                    <span className={`av-status-badge ${client.status.toLowerCase()}`}>
                                                        <span className="av-status-dot"></span>
                                                        {client.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="av-actions" style={{ justifyContent: 'flex-end' }}>
                                                        <button className="av-act-btn" title="Edit Client & Details" onClick={() => onNavigate('client-details', client._id)}>
                                                            <Icon d={icons.edit} size={16} strokeWidth={2.5} />
                                                        </button>
                                                        <button className="av-act-btn delete" title="Delete" onClick={() => handleDelete(client._id)}>
                                                            <Icon d={icons.trash} size={16} strokeWidth={2.5} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {filtered.length > PAGE_SIZE && (
                            <div className="av-pagination">
                                <span className="av-page-info">
                                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                                </span>
                                <div className="av-page-btns">
                                    <button className="av-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                        <Icon d={icons.chevronLeft} size={18} strokeWidth={2.5} />
                                    </button>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <button
                                            key={i}
                                            className={`av-page-btn ${page === i + 1 ? 'active' : ''}`}
                                            onClick={() => setPage(i + 1)}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                    <button className="av-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                        <Icon d={icons.chevronRight} size={18} strokeWidth={2.5} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <form onSubmit={handleSave} style={{ padding: 40 }}>
                        <div className="av-form-grid">
                            <div className="av-form-section">
                                <h3>Company Information</h3>
                                <div className="av-form-section-line"></div>
                            </div>

                            <div className="av-form-group full" style={{ marginBottom: 12 }}>
                                <label style={{ display: 'block', marginBottom: 8, fontSize: '13px', fontWeight: 700, color: '#334155' }}>Company Logo</label>
                                <div style={{ 
                                    display: 'flex', 
                                    gap: 20, 
                                    alignItems: 'center', 
                                    background: '#f8fafc', 
                                    border: '1px dashed #cbd5e1', 
                                    padding: '18px 20px', 
                                    borderRadius: '14px',
                                    width: 'fit-content'
                                }}>
                                    <div style={{ 
                                        width: 64, 
                                        height: 64, 
                                        borderRadius: 12, 
                                        background: '#ffffff', 
                                        border: '1px solid #e2e8f0', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        overflow: 'hidden',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                                    }}>
                                        {(logoFile ? URL.createObjectURL(logoFile) : (form.clientLogo ? `/uploads/${form.clientLogo}` : null)) ? (
                                            <img src={logoFile ? URL.createObjectURL(logoFile) : `/uploads/${form.clientLogo}`} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Icon d={icons.building} size={28} color="#94a3b8" strokeWidth={1.5} />
                                        )}
                                    </div>
                                    <div>
                                        <label 
                                            htmlFor="client-logo-upload"
                                            style={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                gap: 8, 
                                                padding: '8px 16px', 
                                                background: '#ffffff', 
                                                border: '1px solid #cbd5e1', 
                                                borderRadius: '8px', 
                                                fontSize: '13px', 
                                                fontWeight: 700, 
                                                color: '#334155', 
                                                cursor: 'pointer',
                                                transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={e => e.target.style.borderColor = '#2563eb'}
                                            onMouseLeave={e => e.target.style.borderColor = '#cbd5e1'}
                                        >
                                            <Icon d={icons.upload} size={15} strokeWidth={2.2} />
                                            {logoFile ? 'Change Logo' : 'Upload PNG / JPG'}
                                        </label>
                                        <input 
                                            id="client-logo-upload"
                                            type="file" 
                                            accept="image/*" 
                                            onChange={e => setLogoFile(e.target.files[0])} 
                                            style={{ display: 'none' }} 
                                        />
                                        <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: 4 }}>Recommended size 200x200px</div>
                                    </div>
                                </div>
                            </div>

                            <div className="av-form-group full">
                                <label>Company Name</label>
                                <div className="av-input-wrap">
                                    <Icon d={icons.building} size={18} className="av-input-icon" strokeWidth={2.5} />
                                    <input
                                        type="text"
                                        required
                                        value={form.companyName}
                                        onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                                        placeholder="e.g. Infosys Ltd"
                                    />
                                </div>
                            </div>

                            <div className="av-form-group full">
                                <label>Address / Place</label>
                                <div className="av-input-wrap">
                                    <Icon d={icons.mapPin} size={18} className="av-input-icon" strokeWidth={2.5} />
                                    <input
                                        ref={addressInputRef}
                                        type="text"
                                        value={form.address}
                                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                                        placeholder="Company location (Autocomplete)"
                                    />
                                </div>
                            </div>

                            <div className="av-form-group">
                                <label>Industry</label>
                                <select
                                    className="av-select"
                                    value={form.industry}
                                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                                >
                                    <option value="">Select Industry</option>
                                    {industries.map(ind => (
                                        <option key={ind._id} value={ind.name}>{ind.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="av-form-group">
                                <label>Status</label>
                                <select
                                    className="av-select"
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </div>

                            <div className="av-form-section">
                                <h3>Contact Information</h3>
                                <div className="av-form-section-line"></div>
                            </div>

                            <div className="av-form-group">
                                <label>Contact Person</label>
                                <div className="av-input-wrap">
                                    <Icon d={icons.user} size={18} className="av-input-icon" strokeWidth={2.5} />
                                    <input
                                        type="text"
                                        value={form.contactPerson}
                                        onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                                        placeholder="Full name"
                                    />
                                </div>
                            </div>

                            <div className="av-form-group">
                                <label>Email Address</label>
                                <div className="av-input-wrap">
                                    <Icon d={icons.mail} size={18} className="av-input-icon" strokeWidth={2.5} />
                                    <input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="contact@company.com"
                                    />
                                </div>
                            </div>

                            <div className="av-form-group full">
                                <label>Phone Number</label>
                                <div className="phone-input-pair" style={{ display: 'flex', gap: 12 }}>
                                    <select
                                        className="av-select"
                                        style={{ width: 120, flexShrink: 0 }}
                                        value={form.countryCode}
                                        onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                                    >
                                        {Object.values(COUNTRY_CONFIGS).map(c => (
                                            <option key={c.phoneCode} value={c.phoneCode}>{c.phoneCode}</option>
                                        ))}
                                    </select>
                                    <div className="av-input-wrap" style={{ flexGrow: 1 }}>
                                        <input
                                            type="text"
                                            value={form.phone}
                                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            placeholder="Enter phone number"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="av-form-section">
                                <h3>Login Credentials</h3>
                                <div className="av-form-section-line"></div>
                            </div>

                            <div className="av-form-group full">
                                <label>Access Password {editingId && <span style={{ fontWeight: 500, color: '#94a3b8' }}>(Leave blank to keep current)</span>}</label>
                                <div className="av-input-wrap">
                                    <Icon d={icons.lock} size={18} className="av-input-icon" strokeWidth={2.5} />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        placeholder={editingId ? "••••••••" : "Create strong password"}
                                        required={!editingId}
                                    />
                                    <button
                                        type="button"
                                        className="av-eye-btn"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <Icon d={showPassword ? icons.eyeOff : icons.eye} size={18} strokeWidth={2.5} />
                                    </button>
                                </div>
                            </div>

                            {/* Contract & Document Expiry Compliance */}
                            <div className="av-form-section">
                                <h3>Contract & Document Expiry Compliance</h3>
                                <div className="av-form-section-line"></div>
                            </div>

                            {/* Existing Contracts if any */}
                            {editingId && form.contracts && form.contracts.length > 0 && (
                                <div className="av-form-group full" style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8, display: 'block' }}>Active Contracts on Record ({form.contracts.length})</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {form.contracts.map((c, i) => {
                                            const d = new Date();
                                            const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                                            const isExp = c.endDate && String(c.endDate).split('T')[0] < todayStr;
                                            return (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '10px 16px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <Icon d={icons.fileText} size={16} color="#2563eb" strokeWidth={2.2} />
                                                        <span style={{ fontWeight: 700, fontSize: 13.5, color: '#1e293b' }}>{c.title}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <span style={{ fontSize: 12, color: '#64748b' }}>
                                                            End Date: <strong style={{ color: isExp ? '#dc2626' : '#1e293b' }}>{c.endDate ? new Date(c.endDate).toLocaleDateString('en-GB') : 'No expiry'}</strong>
                                                        </span>
                                                        <span style={{
                                                            fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 6,
                                                            background: isExp ? '#fee2e2' : '#dcfce7',
                                                            color: isExp ? '#b91c1c' : '#15803d'
                                                        }}>
                                                            {isExp ? 'Expired' : 'Active'}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Contract Details Form Card */}
                            <div className="av-form-group full" style={{ background: '#f8fafc', padding: '20px 22px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: 8, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon d={icons.fileText} size={15} strokeWidth={2.2} />
                                    </div>
                                    <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{editingId ? 'Add Another Contract / Agreement' : 'Client Contract Agreement'}</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', background: '#e2e8f0', padding: '2px 8px', borderRadius: 99 }}>Optional</span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 5, display: 'block' }}>Contract Title</label>
                                        <input
                                            type="text"
                                            value={contractTitle}
                                            onChange={e => setContractTitle(e.target.value)}
                                            placeholder="e.g. Master Transport Agreement"
                                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', background: '#ffffff' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 5, display: 'block' }}>Start Date</label>
                                        <input
                                            type="date"
                                            value={contractStartDate}
                                            onChange={e => setContractStartDate(e.target.value)}
                                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', background: '#ffffff' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            Contract Expiry Date
                                            <span style={{ fontSize: 10, background: '#fee2e2', color: '#b91c1c', padding: '1px 5px', borderRadius: 4, fontWeight: 800 }}>Radar</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={contractEndDate}
                                            onChange={e => setContractEndDate(e.target.value)}
                                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', background: '#ffffff' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <label
                                        htmlFor="client-contract-file"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px',
                                            background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 8,
                                            fontSize: 12.5, fontWeight: 700, color: '#334155', cursor: 'pointer'
                                        }}
                                    >
                                        <Icon d={icons.upload} size={14} strokeWidth={2.2} />
                                        {contractFile ? contractFile.name : 'Attach Agreement (PDF / DOC)'}
                                    </label>
                                    <input
                                        id="client-contract-file"
                                        type="file"
                                        onChange={e => setContractFile(e.target.files[0])}
                                        style={{ display: 'none' }}
                                    />
                                    {contractFile && (
                                        <button
                                            type="button"
                                            onClick={() => setContractFile(null)}
                                            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                        >
                                            Remove file
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Legal Document Details Form Card */}
                            <div className="av-form-group full" style={{ background: '#f8fafc', padding: '20px 22px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: 8, background: '#fffbeb', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon d={icons.fileText} size={15} strokeWidth={2.2} />
                                    </div>
                                    <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{editingId ? 'Add Compliance Document' : 'Company Legal / Compliance Document'}</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', background: '#e2e8f0', padding: '2px 8px', borderRadius: 99 }}>Optional</span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 5, display: 'block' }}>Document Title</label>
                                        <input
                                            type="text"
                                            value={docTitle}
                                            onChange={e => setDocTitle(e.target.value)}
                                            placeholder="e.g. Commercial Registration (CR)"
                                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', background: '#ffffff' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 5, display: 'block' }}>Document Type</label>
                                        <select
                                            value={docType}
                                            onChange={e => setDocType(e.target.value)}
                                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', background: '#ffffff' }}
                                        >
                                            <option value="Trade License">Trade License</option>
                                            <option value="Commercial Registration">Commercial Registration</option>
                                            <option value="Tax Certificate">Tax Certificate</option>
                                            <option value="KYC Agreement">KYC Agreement</option>
                                            <option value="Other">Other Document</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            Document Expiry Date
                                            <span style={{ fontSize: 10, background: '#fee2e2', color: '#b91c1c', padding: '1px 5px', borderRadius: 4, fontWeight: 800 }}>Radar</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={docExpiryDate}
                                            onChange={e => setDocExpiryDate(e.target.value)}
                                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', background: '#ffffff' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <label
                                        htmlFor="client-doc-file"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px',
                                            background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 8,
                                            fontSize: 12.5, fontWeight: 700, color: '#334155', cursor: 'pointer'
                                        }}
                                    >
                                        <Icon d={icons.upload} size={14} strokeWidth={2.2} />
                                        {docFile ? docFile.name : 'Attach Document (PDF / Image)'}
                                    </label>
                                    <input
                                        id="client-doc-file"
                                        type="file"
                                        onChange={e => setDocFile(e.target.files[0])}
                                        style={{ display: 'none' }}
                                    />
                                    {docFile && (
                                        <button
                                            type="button"
                                            onClick={() => setDocFile(null)}
                                            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                        >
                                            Remove file
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="av-form-group full">
                                <label>Account Notes & Internal Details</label>
                                <textarea
                                    className="av-textarea"
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    placeholder="Add any specific requirements or notes for this client..."
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end', gap: 16, paddingTop: 32, borderTop: '1px solid #f1f5f9' }}>
                            <button type="button" className="av-btn-cancel" onClick={closeModal} disabled={saving}>Discard & Return</button>
                            <button type="submit" className="av-btn-save" disabled={saving}>
                                {saving ? 'Processing...' : editingId ? 'Update Client' : 'Register Client'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

function ClientImportWizard({ operatorId, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActive(e.type === 'dragenter' || e.type === 'dragover');
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setDragActive(false);
        if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
    };
    const handleFileChange = (e) => { if (e.target.files?.[0]) processFile(e.target.files[0]); };

    const processFile = (f) => {
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'binary' });
                const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
                setParsedData(data.map(row => Object.fromEntries(Object.keys(row).map(k => [k.trim(), row[k]]))));
            } catch { alert('Failed to parse Excel file.'); }
        };
        reader.readAsBinaryString(f);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Company Name': 'Test Client Corp',
                'Address': '123 Business Bay, Dubai',
                'Contact Person': 'Jane Smith',
                'Phone': '501122334',
                'Country Code': '+971',
                'Email': 'jane@testclient.com',
                'Industry': 'Technology',
                'Status': 'Active',
                'Notes': 'High priority client'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Clients');
        XLSX.writeFile(wb, 'Clients_Template.xlsx');
    };

    const handleImport = async () => {
        if (!parsedData.length) return;
        setImporting(true);
        try {
            const mapped = parsedData.map(row => ({
                companyName: row['Company Name'],
                address: row['Address'] || '',
                contactPerson: row['Contact Person'] || '',
                phone: row['Phone'] || '',
                countryCode: row['Country Code'] || '+971',
                email: row['Email'] || '',
                industry: row['Industry'] || '',
                status: row['Status'] || 'Active',
                notes: row['Notes'] || '',
                operatorId
            }));
            const res = await fetch('/api/clients/import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operatorId, clients: mapped })
            });
            const data = await res.json();
            if (res.ok) setResult({ createdCount: data.successCount, errors: data.errors || [] });
            else alert(data.message || 'Import failed');
        } catch { alert('Import failed.'); }
        finally { setImporting(false); }
    };

    const S = { page: { padding: 30, background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter',sans-serif" } };

    if (result) return (
        <div style={S.page}>
            <div style={{ background: 'white', borderRadius: 16, padding: 40, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9', maxWidth: 700, margin: '0 auto' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 80, height: 80, borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: 24 }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>Import Completed!</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 500, margin: '0 auto 30px' }}>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#1e40af' }}>{result.createdCount || 0}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginTop: 4 }}>Clients Created</div>
                    </div>
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#92400e' }}>{result.errors?.length || 0}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginTop: 4 }}>Skipped / Errors</div>
                    </div>
                </div>
                <button onClick={onDone} style={{ padding: '12px 30px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Return to Clients</button>
            </div>
        </div>
    );

    return (
        <div style={S.page}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 30 }}>
                <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                </button>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', margin: 0 }}>Import Clients</h1>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Bulk import clients via Excel upload.</p>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 30 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {['Company Name','Address','Contact Person','Phone','Country Code','Email','Industry','Status','Notes'].map((f,i) => (
                                    <span key={i} style={{ fontSize: 11, fontWeight: 600, color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: 6 }}>{f}</span>
                                ))}
                            </div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: 16, border: '1px dashed #cbd5e1', padding: 20, textAlign: 'center' }}>
                            <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px' }}>Download a pre-filled sample template.</p>
                            <button onClick={downloadTemplate} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Download Template</button>
                        </div>
                    </div>
                    <div onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                        onClick={() => document.getElementById('client-file-input').click()}
                        style={{ background: dragActive ? '#f0f7ff' : 'white', borderRadius: 16, border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input id="client-file-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }}/>
                        <div style={{ width: 64, height: 64, borderRadius: 16, background: '#eef2ff', color: '#16539C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        </div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: 12, color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: 6, border: '1px solid #f1f5f9' }}>Supports .xlsx, .xls formats</span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{file?.name}</span>
                            <span style={{ fontSize: 12, color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>{parsedData.length} Rows</span>
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Change File</button>
                            <button onClick={handleImport} disabled={importing} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}</button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: 500 }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Company Name','Contact Person','Email','Status'].map(h => <th key={h} style={{ padding: '14px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>{h}</th>)}
                            </tr></thead>
                            <tbody>
                                {parsedData.map((d,i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: 700, color: '#1e293b' }}>{d['Company Name'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Contact Person'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Email'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Status'] || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
